import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useFetch } from "@/lib/content";
import { useSEO, organizationSchema, faqSchema } from "@/lib/seo";
import { Eyebrow, Reveal, ParallaxImage, CtaButton } from "@/components/site/Motion";
import LogoMarquee from "@/components/site/LogoMarquee";
import CaseCard from "@/components/site/CaseCard";
import Testimonials from "@/components/site/Testimonials";
import NewsStrip from "@/components/site/NewsStrip";
import HomeFaq from "@/components/site/HomeFaq";
import HOME from "@/content/home.json";

const EASE = [0.16, 1, 0.3, 1];
const HERO_IMG =
  "https://images.unsplash.com/photo-1618058313199-9037610ec161?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMGNvbnN1bHRpbmclMjBhYnN0cmFjdHxlbnwwfHx8fDE3OTA0Nzg2ODZ8MA&ixlib=rb-4.1.0&q=85";


function HeroLine({ children, delay }) {
  const reduce = useReducedMotion();
  return (
    <span className="block overflow-hidden">
      <motion.span
        className="block will-change-transform"
        initial={reduce ? { opacity: 1 } : { y: "112%" }}
        animate={reduce ? { opacity: 1 } : { y: "0%" }}
        transition={{ duration: 1.05, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Home() {
  const { data: settings } = useFetch("/content/settings");
  const { data: services } = useFetch("/services");
  const { data: cases } = useFetch("/case-studies");
  const { data: testimonials } = useFetch("/testimonials");
  const { data: clients } = useFetch("/clients");
  const { data: press } = useFetch("/media");

  useSEO({
    title: settings?.seo_title || "RenB Solutions | We Help Companies Grow",
    description: settings?.seo_description || "Business Process Management for ambitious SMEs.",
    path: "/",
    image: settings?.og_image,
    jsonLd: [
      settings ? organizationSchema(settings) : null,
      faqSchema(HOME.faq),
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "RenB Solutions",
        url: typeof window !== "undefined" ? window.location.origin : "",
      },
    ].filter(Boolean),
  });

  const featured = (cases || []).filter((c) => c.featured).slice(0, 3);
  const clientLogos = (clients || []).filter((l) => l.group === "clients");
  const ecosystemLogos = (clients || []).filter((l) => l.group === "ecosystem");

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative border-b border-ash">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-5 pb-16 pt-28 md:px-10 md:pb-20 md:pt-32 lg:grid-cols-12 lg:items-stretch lg:gap-8">
          <div className="lg:col-span-7 lg:self-center">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.15 }}>
              <Eyebrow>We help companies grow</Eyebrow>
            </motion.div>
            <h1
              data-testid="home-hero-heading"
              aria-label="Where process meets progress."
              className="mt-8 font-serif text-[15vw] font-medium leading-[0.98] tracking-[-0.02em] sm:text-7xl lg:text-[6.6rem]"
            >
              <HeroLine delay={0.25}>Where process</HeroLine>
              <HeroLine delay={0.4}>
                meets <em className="text-progress">progress</em>.
              </HeroLine>
            </h1>
            <motion.p
              className="mt-8 max-w-xl text-base leading-relaxed text-obsidian/70 md:text-lg"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
            >
              {HOME.hero_paragraph}
            </motion.p>
            <motion.div
              className="mt-10 flex flex-wrap items-center gap-4"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
            >
              <CtaButton testid="home-hero-cta" href="/book">
                Book a Discovery Call
              </CtaButton>
              <CtaButton testid="home-hero-secondary" variant="outline" href="/case-studies">
                Explore Case Studies
              </CtaButton>
            </motion.div>
          </div>
          <motion.div
            className="hidden lg:col-span-5 lg:block"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
          >
            <ParallaxImage
              src={HERO_IMG}
              alt="Abstract light streaks representing process and progress"
              className="h-full min-h-[520px] border border-ash"
            />
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <section data-testid="stats-band" className="border-b border-ash">
        <div className="mx-auto grid max-w-[1440px] grid-cols-2 gap-px bg-ash px-0 md:grid-cols-4">
          {HOME.stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.06} className="bg-paper p-8 md:p-10">
              <p className="font-serif text-4xl font-semibold tracking-tight md:text-5xl">{s.value}</p>
              <p className="mt-3 text-[12px] leading-snug text-slategray">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CLIENT LOGOS */}
      <LogoMarquee items={clientLogos} label="A glimpse of RenB clients" />
      <LogoMarquee items={ecosystemLogos} label="Our ecosystem partners" testid="ecosystem-logo-marquee" />

      {/* POSITIONING */}
      <section className="border-b border-ash">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-20 md:px-10 md:py-32 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <Eyebrow>The RenB position</Eyebrow>
              <h2 className="mt-8 font-serif text-4xl font-medium leading-[1.08] tracking-tight md:text-6xl">
                {HOME.position_heading}
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-4 lg:col-start-9 lg:self-end">
            <Reveal delay={0.12}>
              <p className="text-base leading-relaxed text-obsidian/70 md:text-lg">
                {HOME.position_paragraph}
              </p>
              <Link
                to="/about"
                data-testid="home-about-link"
                className="group mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-progress"
              >
                Our philosophy
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="border-b border-ash">
        <div className="mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-32">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Eyebrow>What we do</Eyebrow>
              <h2 className="mt-8 font-serif text-4xl font-medium tracking-tight md:text-6xl">Four series. One outcome.</h2>
              <p data-testid="home-frameworks-line" className="mt-6 max-w-2xl text-base leading-relaxed text-obsidian/70">
                {HOME.frameworks_line}{" "}
                <Link to="/frameworks" className="font-bold text-obsidian underline-offset-4 hover:text-progress hover:underline">
                  Explore the frameworks
                </Link>
              </p>
            </div>
            <Link to="/services" data-testid="home-all-services" className="text-sm font-bold uppercase tracking-[0.14em] text-obsidian underline-offset-4 hover:text-progress hover:underline">
              View all services
            </Link>
          </Reveal>
          <div data-testid="services-bento" className="mt-14 grid gap-px border border-ash bg-ash md:grid-cols-2">
            {(services || []).map((s, i) => (
              <Reveal key={s.id} delay={i * 0.07}>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`service-card-${s.slug}`}
                  className="group flex h-full flex-col bg-paper p-8 transition-colors duration-300 hover:bg-obsidian md:p-12"
                >
                  <div className="flex items-start justify-between">
                    <span className="font-serif text-7xl font-medium leading-none text-ash transition-colors duration-300 group-hover:text-progress md:text-8xl">
                      {s.series_code}
                    </span>
                    <span className="text-xs font-bold uppercase tracking-[0.2em] text-slategray transition-colors group-hover:text-paper/50">
                      {s.series_code} Series
                    </span>
                  </div>
                  <div className="mt-16 flex flex-1 flex-col">
                    <h3 className="font-serif text-3xl font-medium tracking-tight transition-colors group-hover:text-paper md:text-4xl">
                      {s.title}
                    </h3>
                    <p className="mt-4 flex-1 max-w-md text-sm leading-relaxed text-obsidian/60 transition-colors group-hover:text-paper/60">
                      {s.excerpt}
                    </p>
                    <span className="mt-6 inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.16em] text-progress">
                      Explore the program <ArrowUpRight size={14} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED CASE STUDIES */}
      {featured.length > 0 && (
        <section className="border-b border-ash">
          <div className="mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-32">
            <Reveal className="flex flex-wrap items-end justify-between gap-6">
              <div>
                <Eyebrow>Proof, not promises</Eyebrow>
                <h2 className="mt-8 font-serif text-4xl font-medium tracking-tight md:text-6xl">Selected case studies</h2>
              </div>
              <Link to="/case-studies" data-testid="home-all-cases" className="text-sm font-bold uppercase tracking-[0.14em] text-obsidian underline-offset-4 hover:text-progress hover:underline">
                All case studies
              </Link>
            </Reveal>
            <div className="mt-14 grid gap-8 md:grid-cols-3">
              {featured.map((c, i) => (
                <Reveal key={c.id} delay={i * 0.08}>
                  <CaseCard item={c} index={i} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* IN THE NEWS */}
      <NewsStrip items={press} />

      {/* TESTIMONIALS */}
      <Testimonials
        items={(testimonials || []).filter((t) => t.featured).slice(0, 3)}
        dark
        moreHref="/testimonials"
        moreLabel={`Read all ${(testimonials || []).length} testimonials`}
      />

      {/* IN BRIEF + FAQ */}
      <HomeFaq items={HOME.faq} brief={HOME.brief} />

      {/* CTA */}
      <section data-testid="home-cta" className="bg-paper">
        <div className="mx-auto max-w-[1440px] px-5 py-24 text-center md:px-10 md:py-36">
          <Reveal>
            <h2 className="mx-auto max-w-4xl font-serif text-5xl font-medium leading-[1.05] tracking-tight md:text-7xl">
              Tell us where it hurts<span className="text-progress">.</span>
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-base text-obsidian/60 md:text-lg">
              A 15-minute discovery call. We&apos;ll tell you what we&apos;d fix first, and whether we&apos;re the right partner for it.
            </p>
            <div className="mt-10">
              <CtaButton testid="home-final-cta" href="/book">
                Book a Discovery Call
              </CtaButton>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
